/**
 * Better Auth client for the Dovod platform API.
 *
 * Sessions live in HTTP-only cookies set by platform-api, so every request is
 * sent with credentials. Invite tokens and passwords are passed through to the
 * API and are never logged or persisted on the client.
 *
 * `~/environments/primary/auth` consumes `platformApiBaseUrl` too: the local
 * server issues no credential of its own, so `~/environments/primary/auth`
 * mints a server token from this same platform-api session
 * (`GET /api/auth/token`) and resolves no second base URL.
 */
import { createAuthClient } from "better-auth/react";

import { browserAccountStorage, forgetAccount, reconcileAccount } from "../../accountStorage";
import {
  clearInferenceCredential,
  revokeInferenceKey,
} from "../../environments/primary/inferenceCredential";
import { currentServerToken } from "../../environments/primary/serverToken";
import { holdReloads } from "../../environments/primary/workspaceState";
import { clearKey } from "./key-store";
import { platformApiBaseUrl } from "./platform-api-origin";
import { onWebAddress } from "./web-address";

// The full path: Better Auth appends `/api/auth` only to a base without one, and
// the web address's base carries `/platform`.
export const authClient = createAuthClient({
  baseURL: `${platformApiBaseUrl}/api/auth`,
  fetchOptions: {
    credentials: "include",
    onSuccess: (context) => {
      if (!onWebAddress()) return;
      const path = new URL(context.request.url).pathname;
      if (!path.endsWith("/sign-in/email") && !path.endsWith("/sign-up/email")) return;
      const data: unknown = context.data;
      if (
        typeof data !== "object" ||
        data === null ||
        !("user" in data) ||
        typeof data.user !== "object" ||
        data.user === null ||
        !("id" in data.user) ||
        typeof data.user.id !== "string"
      )
        return;
      try {
        const reconciliation = reconcileAccount(browserAccountStorage(), data.user.id);
        void reconciliation.cleanup.catch((error: unknown) => {
          console.warn("[dovod] could not erase the browser's account databases", error);
        });
      } catch (error) {
        console.warn("[dovod] could not reconcile the browser's account data", error);
      }
    },
  },
});

export const { signUp, signIn, useSession } = authClient;

/**
 * Ends the session and the inference credential minted for it: revoked at
 * platform-api, then dropped from the server and from `localStorage`.
 *
 * The credential is a `dvk_` outside the session cookie better-auth clears. In
 * `localStorage` it is readable from devtools. In the server a spawned provider
 * has already copied it into its own environment, so a thread left running
 * keeps spending the signed-out account's key.
 *
 * The revoke goes first, while the session that owns the key still exists; it
 * makes every surviving copy worthless. The server is told next: a sign-out that
 * ends with the window's copy gone and the server's copy live is the state this
 * path exists to prevent. Neither step ever rejects — a failed revoke or an
 * unreachable server is logged — so sign-out still completes.
 *
 * A window that holds no server token was admitted by no server — the web
 * router refused its session a workspace, or the server refused the token — so
 * no server holds a key it pushed, and the server is not asked. The server's
 * answer may be the router's refusal, so reloads wait until the sign-out ends:
 * a reload would abort it before the session ends.
 *
 * On the web address an ended session takes the account's data out of the
 * browser (`forgetAccount`): the next account to sign in shares the origin.
 * The caller then loads a fresh document.
 */
export const signOut: typeof authClient.signOut = async (...args) => {
  const releaseReloads = holdReloads();
  try {
    await revokeInferenceKey();
    if (currentServerToken() !== null) await clearInferenceCredential();
    const result = await authClient.signOut(...args);
    const ended = !("error" in result && result.error);
    if (onWebAddress() && ended) {
      void forgetAccount(browserAccountStorage()).catch((error: unknown) => {
        console.warn("[dovod] could not erase the browser's account data", error);
      });
    }
    return result;
  } finally {
    clearKey();
    releaseReloads();
  }
};

/** Input for invite-gated registration. */
export interface SignUpWithInviteInput {
  email: string;
  password: string;
  name: string;
  inviteToken: string;
}

/**
 * Registers a new account against `POST /api/auth/sign-up/email`, carrying
 * the invite token as an extra body field (`fetchOptions.body` merges into
 * the request body ahead of dispatch). On failure the server responds with
 * a machine-readable code (`INVITE_INVALID | INVITE_USED | INVITE_EXPIRED`
 * or a Better Auth base code) surfaced on the returned `error`.
 */
export function signUpWithInvite(input: SignUpWithInviteInput) {
  return signUp.email({
    email: input.email,
    password: input.password,
    name: input.name,
    fetchOptions: { body: { inviteToken: input.inviteToken } },
  });
}
